import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Badge } from "./ui/badge";
import { useNavigate } from "react-router-dom";

const Projects = () => {
  const navigate = useNavigate();
  
  // Category color mapping
  const categoryColors = {
    HRTech: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    AdTech: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    FinTech: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    Crypto: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    IoT: "bg-red-500/20 text-red-400 border-red-500/30",
  };
  
  const projects = [
    {
      id: 1,
      title: "Yelp Mentorship Platform",
      category: "HRTech",
      description: "A Scalable Mentorship System for Entrepreneurial Growth",
      image: "https://customer-assets.emergentagent.com/job_web-creator-900/artifacts/vnls4t6f_Gemini_Generated_Image_xn60aoxn60aoxn60.png",
      link: "/projects/yelp-mentorship-platform",
    },
    {
      id: 2,
      title: "Cardinal",
      category: "FinTech",
      description: "A Credit-Card Companion for Canadians — Never Miss a Payment, Always Tap the Right Card",
      image: "https://static.prod-images.emergentagent.com/jobs/a6b39c99-3f10-4c03-92e5-f1e634642890/images/81c2239695e68c37f9ab3edc2f7f625b28025f1d8cd36f0d420b7eccb46feb54.jpeg",
      link: "/projects/cardinal",
    },
    {
      id: 3,
      title: "FinTell",
      category: "FinTech",
      description: "Modernizing Financial Advisor Networking Through NFC-Enabled Digital Business Cards",
      image: "https://customer-assets.emergentagent.com/job_web-creator-900/artifacts/mi9zg57s_Gemini_Generated_Image_uuwh80uuwh80uuwh.png",
      link: "/projects/fintell",
    },
    {
      id: 4,
      title: "CryptoDalal",
      category: "Crypto",
      description: "Bridging India's Cryptocurrency Knowledge Gap Through Expert Portfolio Management",
      image: "https://customer-assets.emergentagent.com/job_web-creator-900/artifacts/plis2unz_Gemini_Generated_Image_j4dxuoj4dxuoj4dx.png",
      link: "/projects/cryptodalal",
    },
    {
      id: 5,
      title: "Bare Organic",
      category: "IoT",
      description: "Smart Hydroponic System for Space-Constrained Urban Living",
      image: "https://customer-assets.emergentagent.com/job_web-creator-900/artifacts/tmn69cpk_untitled%205.png",
      link: "/projects/bare-organic",
    },
    {
      id: 6,
      title: "Adeefly",
      category: "AdTech",
      description: "Democratizing Outdoor Advertising Through Mobile LED Networks",
      image: "https://customer-assets.emergentagent.com/job_web-creator-900/artifacts/ie0akgpz_Gemini_Generated_Image_9y3bso9y3bso9y3b.png",
      link: "/projects/adeefly",
    },
  ];

  const handleProjectClick = (project) => {
    if (project.link) {
      navigate(project.link);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section id="projects" className="relative py-24 md:py-32 overflow-hidden">
      {/* Geometric elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute border border-white/[0.04] rounded-full" style={{ width: 350, height: 350, right: '-5%', top: '10%' }} />
        <div className="absolute w-px bg-gradient-to-b from-transparent via-white/[0.06] to-transparent" style={{ height: '40%', left: '5%', top: '20%' }} />
        <div className="absolute" style={{ right: '8%', bottom: '15%' }}>
          <div className="w-8 h-px bg-white/[0.08]" />
          <div className="w-px h-8 bg-white/[0.08] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="absolute flex gap-3" style={{ left: '15%', bottom: '10%' }}>
          <div className="w-1.5 h-1.5 rounded-full bg-white/[0.12]" />
          <div className="w-1.5 h-1.5 rounded-full bg-white/[0.08]" />
          <div className="w-1.5 h-1.5 rounded-full bg-white/[0.05]" />
        </div>
        <div className="absolute border border-purple-400/[0.08]" style={{ width: 14, height: 14, left: '50%', bottom: '8%', transform: 'rotate(45deg)' }} />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-sm font-mono text-blue-400 tracking-wider uppercase mb-4">
            Featured projects
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] tracking-tight text-white">
            Solutions built for{" "}
            <span className="text-gradient">scale</span>
          </h2>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="projects-grid"
        >
          {projects.map((project) => (
            <motion.div
              key={project.id}
              variants={itemVariants}
              onClick={() => handleProjectClick(project)}
              className="project-card group relative overflow-hidden rounded-3xl bg-[#121212] border border-white/5 hover:border-white/10 transition-all duration-300"
              data-testid={`project-card-${project.id}`}
            >
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
              </div>

              {/* Content */}
              <div className="relative p-6 pt-4">
                {/* Category - Color coded */}
                <Badge className={`mb-3 text-xs font-medium ${categoryColors[project.category] || "bg-white/10 text-white/60 border-white/20"}`}>
                  {project.category}
                </Badge>

                {/* Title */}
                <h3 className="text-xl font-bold font-['Syne'] text-white mb-2 group-hover:text-blue-400 transition-colors duration-300">
                  {project.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-white/60 line-clamp-2">
                  {project.description}
                </p>

                {/* Arrow */}
                <div className="absolute top-4 right-6 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:bg-white/10">
                  <ArrowUpRight className="w-5 h-5 text-white" />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
